import React, { useEffect, useState } from 'react';
import { Box, Text, FormControl, FormLabel, Icon, useDisclosure, VStack } from '@chakra-ui/react';
import Select from '../../../components/themeComponents/react-chakra-select';
import { useQuery } from 'react-query';
import { fetchEmailChannels, fetchTemplate, getSmsGatways } from '../../../Services/api';
import { getTemplateRatioIncrementer, prepareGatewayList, prepareTemplateList } from '../../../_helpers/data_helper';
import { BsSend } from 'react-icons/bs';
import { FaEye } from 'react-icons/fa';
import { BiTrash } from 'react-icons/bi';
import _ from 'lodash';
import SendTestModal from '../../../components/SendTestModal';
import { useLocation } from 'react-router-dom';
import PrimaryButton from '../../../components/themeComponents/PrimaryButton';
import TemplatePreviewModal from './TemplatePreviewModal';
import ABTestMobilePreview from './ABTestMobilePreview';

function TemplateSelection({
  selectedChannel,
  orgToken,
  formik,
  targetRatios,
  setTargetRatios,
  isValidRatios,
  setIsValidRatios,
}) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [selectedGatewayUuid, setSelectedGatewayUuid] = useState('');
  const location = useLocation();

  // Ensure campaignType has a safe default
  useEffect(() => {
    if (!formik.values.campaignType) {
      formik.setFieldValue('campaignType', 'BROADCAST_V2');
      formik.setFieldValue('broadcastType', 'BROADCAST_V2');
      formik.setFieldValue('broadcast', 'BROADCAST_V2');
    }
  }, [formik.values.campaignType]);

  const templateQuery = useQuery(
    ['fetchTemplate', selectedChannel, selectedGatewayUuid],
    async () => {
      const channel = selectedChannel || formik.values.selectedChannel;
      if (!channel) return [];

      const res = await fetchTemplate(
        channel,
        orgToken,
        selectedGatewayUuid,
        formik.values.selectedWhatsappProvider
      );

      const templates = prepareTemplateList(channel, res?.data || []);

      if (!location?.state?.data?.campaignData && templates.length > 0) {
        const first = templates[0];
        formik.setFieldValue('selectedTempName', first.label);
        formik.setFieldValue('selectedTemp', first.uuid);
        formik.setFieldValue('subjectLine', first.subject || '');
        formik.setFieldValue(`scheduleData.targetRatios[0].templateData.uuid`, first.uuid);
        formik.setFieldValue(`scheduleData.targetRatios[0].templateData.name`, first.label);
      }

      return templates;
    },
    { enabled: !!(selectedChannel || formik.values.selectedChannel) }
  );

  const gatewayQuery = useQuery(
    ['gatways', selectedChannel],
    async () => {
      const channel = selectedChannel || formik.values.selectedChannel;
      if (channel === 'sms') {
        const res = await getSmsGatways();
        return res.data;
      }
      if (channel === 'email') {
        const res = await fetchEmailChannels(orgToken);
        return res?.data?.data;
      }
      return null;
    },
    { enabled: !!(selectedChannel || formik.values.selectedChannel) && ['sms', 'email'].includes(selectedChannel || formik.values.selectedChannel) }
  );

  // Load from existing campaign
  useEffect(() => {
    if (!location?.state?.data?.campaignData || !templateQuery.data) return;

    const campaign = location.state.data.campaignData;
    const ratiosObj = {};

    campaign.scheduleData?.targetRatios?.forEach((item, i) => {
      ratiosObj[i] = item.ratio || 0;

      formik.setFieldValue(`scheduleData.targetRatios[${i}].templateData.uuid`, item.templateData?.uuid);
      formik.setFieldValue(`scheduleData.targetRatios[${i}].templateData.name`, item.templateData?.name);
      formik.setFieldValue(`scheduleData.targetRatios[${i}].ratio`, item.ratio);

      if (i === 0) {
        formik.setFieldValue('selectedTemp', item.templateData?.uuid);
        formik.setFieldValue('selectedTempName', item.templateData?.name);
        formik.setFieldValue('subjectLine', item.templateData?.subject || '');
      }
    });

    setTargetRatios(Object.keys(ratiosObj).length > 0 ? ratiosObj : { 0: 100 });

    if (campaign.selectedGateway) {
      formik.setFieldValue('selectedGateway', campaign.selectedGateway);
      setSelectedGatewayUuid(campaign.selectedGateway);
    }
  }, [templateQuery.data, location?.state?.data?.campaignData, formik, setTargetRatios]);

  const openPreview = (uuid) => {
    if (!uuid || !templateQuery.data) return;
    const found = _.find(templateQuery.data, { uuid });
    if (found) {
      setPreviewTemplate(found);
      onOpen();
    }
  };

  const handleSelectTemplate = (option, index) => {
    if (!option?.uuid) return;

    const isSingleMode = targetRatios['0'] === 100;

    if (isSingleMode) {
      formik.setFieldValue('selectedTemp', option.uuid);
      formik.setFieldValue('selectedTempName', option.label);
      formik.setFieldValue('subjectLine', option.subject || '');
      formik.setFieldValue(`scheduleData.targetRatios[0].templateData.uuid`, option.uuid);
      formik.setFieldValue(`scheduleData.targetRatios[0].templateData.name`, option.label);
    } else {
      formik.setFieldValue(`scheduleData.targetRatios[${index}].templateData.uuid`, option.uuid);
      formik.setFieldValue(`scheduleData.targetRatios[${index}].templateData.name`, option.label);
    }

    if ((selectedChannel || formik.values.selectedChannel) === 'email') {
      formik.setFieldValue('tempBody', option.body || '');
    }
  };

  const handleTargetRatioChange = (index, value) => {
    const clamped = Math.max(0, Math.min(100, Math.floor(Number(value) || 0)));

    setTargetRatios((prev) => {
      const next = { ...prev, [index]: clamped };
      const sum = Object.values(next).reduce((a, b) => a + b, 0);
      setIsValidRatios(sum === 100);

      Object.entries(next).forEach(([i, r]) => {
        formik.setFieldValue(`scheduleData.targetRatios[${i}].ratio`, r);
      });

      return next;
    });

    const isMulti = Object.values(targetRatios).some((v) => v < 100) || clamped < 100;
    const type = isMulti ? 'multiple_schedule_v2' : 'BROADCAST_V2';
    formik.setFieldValue('campaignType', type);
    formik.setFieldValue('broadcastType', type);
    formik.setFieldValue('broadcast', type);
  };

  const handleRemoveTargetRatio = (indexToRemove) => {
    setTargetRatios((prev) => {
      const next = { ...prev };
      const removed = next[indexToRemove] || 0;
      delete next[indexToRemove];

      const keys = Object.keys(next).map(Number).sort((a, b) => a - b);
      let totalLeft = keys.reduce((sum, k) => sum + next[k], 0);

      if (totalLeft > 0) {
        keys.forEach((k) => {
          next[k] = Math.floor((next[k] / totalLeft) * (totalLeft + removed));
        });
      } else if (keys.length > 0) {
        const share = Math.floor(removed / keys.length);
        keys.forEach((k) => (next[k] += share));
      }

      let sum = Object.values(next).reduce((a, b) => a + b, 0);
      if (sum !== 100 && keys.length > 0) {
        next[keys[keys.length - 1]] += 100 - sum;
      }

      const updated = keys.map((i) => ({ ratio: next[i] }));
      formik.setFieldValue('scheduleData.targetRatios', updated);

      return next;
    });
  };

  const addNewRatio = () => {
    if (Object.keys(targetRatios).length >= 20) return;

    const newIndex = Object.keys(targetRatios).length;
    const firstUuid = formik.values.scheduleData?.targetRatios?.[0]?.templateData?.uuid || '';
    const firstName = formik.values.scheduleData?.targetRatios?.[0]?.templateData?.name || '';

    setTargetRatios((prev) => ({ ...prev, [newIndex]: 0 }));

    formik.setFieldValue(`scheduleData.targetRatios[${newIndex}].templateData.uuid`, firstUuid);
    formik.setFieldValue(`scheduleData.targetRatios[${newIndex}].templateData.name`, firstName);

    formik.setFieldValue('campaignType', 'multiple_schedule_v2');
    formik.setFieldValue('broadcastType', 'multiple_schedule_v2');
    formik.setFieldValue('broadcast', 'multiple_schedule_v2');
  };

  const channel = selectedChannel || formik.values.selectedChannel || '';
  const isMultiMode = targetRatios['0'] !== 100;
  const campaignTypeLower = (formik.values.campaignType || '').toLowerCase();

  return (
    <>
      <Box w="100%" m="0 auto" mt={5} p={3} bg="white" height="550px" overflowY="scroll">
        <Text color="heading" as="b">
          Template Information
        </Text>

        {channel !== 'inapp' && channel !== 'mobile' && (
          <Text
            mb="15px"
            float="right"
            cursor="pointer"
            textDecoration="underline"
            onClick={() => setIsTestModalOpen(true)}
          >
            Send Test Communication
            <Icon as={BsSend} cursor="pointer" title={`Send Test ${channel}`} ml="10px" />
          </Text>
        )}

        {channel === 'sms' && (
          <FormControl mb={4}>
            <FormLabel fontWeight="600" color="inputLabel">
              Select Provider
            </FormLabel>
            {gatewayQuery.data && (
              <Select
                value={_.filter(prepareGatewayList(gatewayQuery.data?.gatewayList || []), {
                  value: formik.values.selectedGateway,
                })}
                options={prepareGatewayList(gatewayQuery.data?.gatewayList || [])}
                onChange={(e) => {
                  formik.setFieldValue('selectedGateway', e.value);
                  setSelectedGatewayUuid(e.value);
                }}
              />
            )}
          </FormControl>
        )}

        <VStack gap={6} mt={4}>
          {/* Send Test Modal */}
          <SendTestModal
            isOpen={isTestModalOpen}
            onClose={() => setIsTestModalOpen(false)}
            orgToken={orgToken}
            uuid={formik.values.selectedTemp}
            selectedSmtpUuid={formik?.values?.selectedSmtpUuid}
            formik={formik}
            channel={channel}
          />

          {Object.keys(targetRatios).map((key) => {
            const index = Number(key);
            const currentUuid =
              isMultiMode || index > 0
                ? formik.values.scheduleData?.targetRatios?.[index]?.templateData?.uuid
                : formik.values.selectedTemp;

            return (
              <Box key={key} p={4} bg="white" w="60%" borderWidth="1px" borderRadius="md" pos="relative">
                <FormControl>
                  <FormLabel color="inputLabel">Template</FormLabel>
                  <Select
                    value={_.find(templateQuery.data || [], { uuid: currentUuid }) || null}
                    options={templateQuery.data || []}
                    onChange={(opt) => handleSelectTemplate(opt, index)}
                  />

                  {currentUuid && (
                    <Icon
                      as={FaEye}
                      pos="absolute"
                      right="12px"
                      top="70px"
                      cursor="pointer"
                      onClick={() => openPreview(currentUuid)}
                    />
                  )}
                </FormControl>

                {(isMultiMode || campaignTypeLower.includes('multiple') || campaignTypeLower === 'broadcast') && (
                  <FormControl mt={4}>
                    <FormLabel fontWeight="600" color="inputLabel" display="flex" alignItems="center">
                      Target Ratio
                      <Box ml={4} w="240px">
                        <Select
                          value={_.find(getTemplateRatioIncrementer(), { value: targetRatios[index] }) || null}
                          options={getTemplateRatioIncrementer()}
                          onChange={(e) => handleTargetRatioChange(index, e.value)}
                        />
                      </Box>
                    </FormLabel>
                  </FormControl>
                )}

                {index !== 0 && (
                  <Icon
                    as={BiTrash}
                    pos="absolute"
                    right="12px"
                    bottom="12px"
                    fontSize="20px"
                    cursor="pointer"
                    color="red.500"
                    onClick={() => handleRemoveTargetRatio(index)}
                  />
                )}
              </Box>
            );
          })}

          {!isValidRatios && (
            <Text color="red.500" fontSize="sm">
              Total target ratios must equal 100%
            </Text>
          )}

          {channel !== 'inapp' && channel !== 'mobile' && (
            <PrimaryButton mt={4} label="Add Ratio" onClick={addNewRatio} />
          )}
        </VStack>
      </Box>

      {/* Preview Modals */}
      {previewTemplate && (
        <>
          {channel === 'whatsapp' ? (
            <ABTestMobilePreview
              isOpen={isOpen}
              onClose={() => {
                onClose();
                setPreviewTemplate(null);
              }}
              channel="whatsapp"
              message={previewTemplate?.dt?.message}
              attachment={previewTemplate?.dt?.attachment}
              buttons={previewTemplate?.dt?.buttons}
            />
          ) : channel === 'sms' ? (
            <ABTestMobilePreview
              isOpen={isOpen}
              onClose={() => {
                onClose();
                setPreviewTemplate(null);
              }}
              channel="sms"
              message={previewTemplate?.dt?.message}
            />
          ) : (
            <TemplatePreviewModal
              isOpen={isOpen}
              onClose={() => {
                onClose();
                setPreviewTemplate(null);
              }}
              commonSubjectLine={formik.values.subjectLine}
              tempBody={previewTemplate?.body || formik.values.tempBody || ''}
            />
          )}
        </>
      )}
    </>
  );
}

export default TemplateSelection;